import { styled } from "@mui/material";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ForumParticipant from "messaging/forums/subscribers/forum-participant";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { UserAvatarMap } from "messaging/forums/_views/components/forum-chat-window";
import Message from "messaging/message";
import React from "react";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import MessageHistoryControl from "messaging/_views/components/message-history-dialog";
import EditedMark from "messaging/correspondence/_views/edited-mark";
import { MessageBubble, MessageBubbleFiles, NO_BODY_TEXT } from "messaging/correspondence/_views/message-bubble";
import BubbleTimestamp from "messaging/correspondence/_views/bubble-timestamp";
import { ReadStateButton } from "messaging/correspondence/_views/read-state-controls";
import { SystemNotice } from "messaging/correspondence/_views/system-notice";

/** Someone who has left the conversation keeps their name, muted beside those still in it (#6878). */
const SenderName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "hasLeft",
})<{ hasLeft?: boolean }>(({ theme, hasLeft }) => ({
  whiteSpace: "nowrap",
  ...(hasLeft && { color: theme.palette.text.secondary }),
}));
const SubjectChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
const DeletedMessageText = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  fontStyle: "italic",
}));
type ReceivedMessageProps = {
  message: Message;
  /** Who wrote the message, as the forum names them. Absent when the forum cannot name them at all. */
  sender?: ForumParticipant;
  avatars?: UserAvatarMap;
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick: (message: Message, image: MessageFileInfo) => void;
  onDownload: (message: Message, attachment: MessageFileInfo) => void;
  /** Offers Copy to documents on each of the message's files (#6053). */
  onCopyToDocuments?: (message: Message, attachment: MessageFileInfo) => void;
  /** The reader has not read this message: its bubble carries the unread rule, and a press marks it read. */
  unread?: boolean;
  /** Offers the read-state button. Pressed, it marks the message read, or unread when it is read. */
  onReadStateToggled?: (message: Message) => void;
  readStateDisabled?: boolean;
};

export const ReceivedMessage = React.forwardRef<
  HTMLDivElement,
  ReceivedMessageProps & React.HTMLAttributes<HTMLDivElement>
>(function ReceivedMessage(props, ref) {
  const {
    message,
    sender,
    avatars,
    onImageLoad,
    onImageClick,
    onDownload,
    onCopyToDocuments,
    unread,
    onReadStateToggled,
    readStateDisabled,
    ...otherProps
  } = props;
  const isLive = !message.isDeleted && !message.retracted;
  const isUnread = Boolean(unread) && isLive;

  const isRemoved = message.isDeleted || Boolean(message.retracted);
  const historyLabel = message.isDeleted ? "Deleted" : message.retracted ? "Removed" : "Edited";
  const senderFullName = sender?.displayName ?? "Unknown Sender";

  return (
    <>
      {message?.senderId == null ? (
        <SystemNotice
          ref={ref}
          published={message.published}
          status={
            !message.isDeleted &&
            (message.retracted || message.updated) && (
              <MessageHistoryControl message={message} label={historyLabel} canViewHistory={!isRemoved} />
            )
          }
          readState={
            onReadStateToggled &&
            isLive && (
              <ReadStateButton unread={isUnread} disabled={readStateDisabled} onToggle={() => onReadStateToggled(message)} />
            )
          }
          unread={isUnread}
          onMarkRead={onReadStateToggled ? () => onReadStateToggled(message) : undefined}
          {...otherProps}
        >
            {message.isDeleted ? (
              <DeletedMessageText>This message was deleted</DeletedMessageText>
            ) : message.hasNoBody ? (
              <DeletedMessageText>{NO_BODY_TEXT}</DeletedMessageText>
            ) : (
              <MessageMarkdownContent content={message.content ?? ""} />
            )}
            {/* A file shared with a notice is drawn as one shared in a message is. */}
            <MessageBubbleFiles
              side="received"
              files={message.attachments ?? []}
              forumId={message.forum.id?.value}
              messageId={message.id?.value}
              onImageLoad={onImageLoad}
              onImageClick={(image) => onImageClick(message, image)}
              onDownload={(file) => onDownload(message, file)}
              onCopyToDocuments={onCopyToDocuments ? (file) => onCopyToDocuments(message, file) : undefined}
            />
        </SystemNotice>
      ) : (
        <MessageBubble
          ref={ref}
          side="received"
          avatarSrc={avatars?.[message?.senderId.value]}
          senderName={
            // The info row cuts a long name short; the whole of it shows under the pointer.
            <SenderName title={senderFullName} hasLeft={sender?.hasLeft} data-sender-has-left={sender?.hasLeft || undefined}>
              {senderFullName}
            </SenderName>
          }
          time={message.published && <BubbleTimestamp date={message.published} />}
          unread={isUnread}
          onMarkRead={onReadStateToggled ? () => onReadStateToggled(message) : undefined}
          controls={
            onReadStateToggled &&
            isLive && (
              <ReadStateButton unread={isUnread} disabled={readStateDisabled} onToggle={() => onReadStateToggled(message)} />
            )
          }
          status={
            message.isDeleted ? null : message.retracted ? (
              <MessageHistoryControl message={message} label={historyLabel} canViewHistory={false} />
            ) : message.updated ? (
              <>
                <EditedMark message={message} />
                <MessageHistoryControl message={message} label="Edited" />
              </>
            ) : null
          }
          {...otherProps}
        >
          <div>
            {message?.senderId && message.subject && message.subject.length > 0 && (
              <SubjectChip label={`RE: ${message.subject}`} size="small" />
            )}
          </div>
          {message.isDeleted ? (
            <DeletedMessageText>This message was deleted</DeletedMessageText>
          ) : message.hasNoBody ? (
            <DeletedMessageText>{NO_BODY_TEXT}</DeletedMessageText>
          ) : (
            <MessageMarkdownContent content={message.content ?? ""} />
          )}
          <MessageBubbleFiles
            side="received"
            files={message.attachments ?? []}
            forumId={message.forum.id?.value}
            messageId={message.id?.value}
            onImageLoad={onImageLoad}
            onImageClick={(image) => onImageClick(message, image)}
            onDownload={(file) => onDownload(message, file)}
            onCopyToDocuments={onCopyToDocuments ? (file) => onCopyToDocuments(message, file) : undefined}
          />
        </MessageBubble>
      )}
    </>
  );
});
